// Mijn gedeelde selecties: wat staat er uit, wie keek, en intrekken als het
// klaar is. Intrekken is definitief (de link geeft daarna niets meer, ook
// doorgestuurd), dus daar zit een bevestiging voor.
import { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Tooltip, Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BlockIcon from '@mui/icons-material/Block';
import toast from 'react-hot-toast';
import { AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';

const API = `${AI_ENGINE_URL}/api/v1/inspiration`;

const datum = (iso) => (iso ? new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }) : '—');

function status(d) {
  if (d.ingetrokken) return { label: 'Ingetrokken', color: 'default' };
  if (d.verlopen) return { label: 'Verlopen', color: 'default' };
  return { label: `Actief tot ${datum(d.verlooptOp)}`, color: 'success' };
}

export default function GedeeldeLinksDialog({ onClose }) {
  const [links, setLinks] = useState(null);

  const laad = useCallback(async () => {
    try {
      const r = await authFetch(`${API}/shares`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setLinks(d.shares || []);
    } catch (e) { toast.error(`Links laden mislukt: ${e.message}`); setLinks([]); }
  }, []);
  useEffect(() => { laad(); }, [laad]);

  const kopieer = async (tekst) => {
    try { await navigator.clipboard.writeText(tekst); toast.success('Link gekopieerd'); }
    catch { toast.error('Kopiëren lukte niet'); }
  };

  const trekIn = async (d) => {
    const ok = await bevestig({
      titel: 'Link intrekken?',
      tekst: `"${d.titel}" geeft daarna niets meer, ook niet bij wie de link al heeft. Dit is niet terug te draaien.`,
      knop: 'Intrekken', gevaarlijk: true,
    });
    if (!ok) return;
    try {
      const r = await authFetch(`${API}/shares/${d.id}`, { method: 'DELETE' });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || `HTTP ${r.status}`); }
      setLinks((oud) => oud.map((x) => (x.id === d.id ? { ...x, ingetrokken: true } : x)));
      toast.success('Link ingetrokken');
    } catch (e) { toast.error(`Intrekken mislukt: ${e.message}`); }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Mijn gedeelde selecties</DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        {links === null && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>}
        {links?.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            Je hebt nog niets gedeeld. Vink video's aan in het raster en kies "Deel met klant".
          </Typography>
        )}
        {links?.map((d) => {
          const st = status(d);
          const actief = !d.ingetrokken && !d.verlopen;
          return (
            <Box key={d.id} sx={{
              display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto auto', alignItems: 'center',
              gap: 1.5, py: 1.25, borderBottom: '1px solid', borderColor: 'divider',
              opacity: actief ? 1 : 0.6,
            }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>{d.titel}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {d.aantalVideos} video{d.aantalVideos === 1 ? '' : "'s"} · gemaakt {datum(d.op)} ·{' '}
                  {d.aantalWeergaven} keer bekeken{d.laatstBekeken ? `, laatst ${datum(d.laatstBekeken)}` : ''}
                  {d.magDownloaden ? ' · downloaden aan' : ''}{d.collectionId ? ' · groeit mee met verzameling' : ''}
                </Typography>
              </Box>
              <Chip size="small" label={st.label} color={st.color} variant={actief ? 'filled' : 'outlined'} />
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Link kopiëren"><span>
                  <IconButton size="small" disabled={!actief} onClick={() => kopieer(d.link)}><ContentCopyIcon fontSize="small" /></IconButton>
                </span></Tooltip>
                <Tooltip title="Intrekken"><span>
                  <IconButton size="small" disabled={!actief} onClick={() => trekIn(d)} color="error"><BlockIcon fontSize="small" /></IconButton>
                </span></Tooltip>
              </Box>
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Sluiten</Button>
      </DialogActions>
    </Dialog>
  );
}
